import { FC, useState, useEffect } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SCUIButton } from '../../atomic/atoms';
import { SCUICustomDataGrid, SCUIGeneralDrawer, SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUILocalizationFilter } from './components';
import {
    Box,
    Fade,
    IconButton,
    LinearProgress,
    Menu,
    TextField,
    Paper,
    Grid,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
} from '@mui/material';
import billingInfoImage from '../../../src/assets/icons/svg/pageIcons/billingInfo.svg';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { initialState } from './LocalizationManagement.helper';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { SCUILocalizationEdit } from './components/SCUILocalizationEdit/SCUILocalizationEdit';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { SCUIConfirmDialog } from './components/SCUIConfirmDialog/SCUIConfirmDialog';
import Swal from 'sweetalert2';
import { LocalizationManagementQuery } from '../../services/query';
import { LocalizationManagementCommand } from '../../services/command';
import { SCUIAddLocalizationDrawerContent } from './components/SCUIAddLocalizationDrawerContent';
import { LOC, locCls, locConfirmDialogE2e } from './localizationManagement.classes';

interface ILocalizationListData {
    key: number;
    label: string;
}

export const LocalizationManagement: FC<any> = () => {
    const { t } = useTranslation();
    const localizationList: ILocalizationListData[] = [];

    const [filterInput, setFilterInput] = useState(initialState.localizationFilters);
    const [appId, setAppId] = useState(1);
    const [cultureCode, setCultureCode] = useState<string>('tr-TR');
    const [keyword, setKeyword] = useState<string>('');
    const [datas, setDatas] = useState<any>();
    const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
    const [drawerEditOpen, setDrawerEditOpen] = useState(false);
    const [applicationList, setApplicationList] = useState([]);
    const [cultureList, setCultureList] = useState([]);
    const [selectedLocalizationItem, setSelectedLocalizationItem] = useState<any>();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [actionType, setActionType] = useState<'update' | 'delete' | 'fileUpload'>('delete');
    const [dialogTitle, setDialogTitle] = useState<string>('');
    const [dialogContent, setDialogContent] = useState<string>('');
    const [loading, setLoading] = useState<any>(false);
    const [state, setState] = useState({
        right: false,
    });
    const [pageSize, setPageSize] = useState(20);
    const [rowCount, setRowCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(0);
    const [refresh, setRefresh] = useState(false);

    const onDrawerFilterOpen = () => setDrawerFilterOpen(true);
    const onDrawerFilterClose = () => setDrawerFilterOpen(false);
    const onDrawerEditOpen = () => setDrawerEditOpen(true);
    const onDrawerEditClose = () => setDrawerEditOpen(false);
    const onDrawerOpen = () => setState({ ...state, right: true });
    const onDrawerClose = () => setState({ ...state, right: false });
    const refreshFunc = () => {
        setRefresh((prevRefreshing) => !prevRefreshing);
    };

    useEffect(() => {
        if (filterInput.cultureCode !== '-1' || filterInput.appId !== -1) getLocalizationList(filterInput);
    }, [refresh, pageNumber, filterInput]);

    const ListItem = styled('li')(({ theme }) => ({
        margin: theme.spacing(0.5),
    }));

    useEffect(() => {
        GetCultureList();
        GetApplicationList();
    }, []);
    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };
    const handlePageSize = (i: any) => {
        setPageSize(i);
    };
    const handlePageNumber = (i: any) => {
        setPageNumber(i);
    };
    const handleClickOpenConfirmDialog = (
        selectedRow: any,
        actionType: 'update' | 'delete' | 'fileUpload',
        dialogTitle: string,
        dialogContent: string
    ) => {
        SetLocalizationItem(selectedRow);
        setActionType(actionType);
        setDialogTitle(dialogTitle);
        setDialogContent(dialogContent);
        setOpenConfirmDialog(true);
    };
    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'id', flex: 1, hide: true },
        { field: 'applicationNames', headerName: t('general.app'), flex: 1 },
        { field: 'keyword', headerName: t('general.keyword'), flex: 1 },
        { field: 'keywords', headerName: t('general.keywords'), flex: 1 },
        {
            field: 'actions ',
            headerName: t('general.actions'),
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Box className={LOC.datagridActionsCell}>
                        <PopupState variant="popover" popupId="demo-popup-menu">
                            {(popupState: any) => (
                                <>
                                    <IconButton {...bindTrigger(popupState)}>
                                        <MoreVertIcon className={locCls('svg_icons', LOC.inputIconRowActionsMenu)} />
                                    </IconButton>
                                <Menu
                                    {...bindMenu(popupState)}
                                    slotProps={{ paper: { className: LOC.rowActionsMenuPaper } }}
                                >
                                    <MenuItem
                                        className={LOC.inputMenuItemRowEdit}
                                        onClick={() => onDrawerModalEditOpen(params.row)}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                    <MenuItem
                                        className={LOC.inputMenuItemRowDelete}
                                        onClick={() =>
                                            handleClickOpenConfirmDialog(
                                                params.row,
                                                'delete',
                                                '',
                                                t('general.deleteConfirmationText')
                                            )
                                        }
                                    >
                                        {t('general.delete')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                    </Box>
                );
            },
        },
    ];
    const getLocalizationList = async (filterInput?: any) => {
        try {
            if (!loading) {
                setLoading(true);
                let filterData: any = {
                    filterText: filterInput.keyword,
                    culture: filterInput.cultureCode,
                    applicationId: filterInput.appId,
                    pageSize: pageSize,
                    page: filterInput.page + 1,
                };
                onDrawerFilterClose();
                let response: any =
                    await LocalizationManagementCommand.GetLocalizationManagementListByFilter(filterData);
                console.info('response:', response);
                if (response && response.localizationItems && response.localizationItems.length > 0) {
                    let parseData = response.localizationItems.map((item: any) => {
                        return {
                            id: item.keywordId,
                            applicationNames: item.applicationNames,
                            applications: item.applications,
                            keyword: item.keyword,
                            keywords: item.keywordValue,
                            keywordId: item.keywordId,
                        };
                    });
                    setDatas(parseData);
                    setRowCount(response.pageInfo.totalCount);
                } else {
                    Swal.fire('Error', t('general.noDataFound'), 'error');
                    setDatas([]);
                }
                setLoading(false);
            }
        } catch (error: any) {
            Swal.fire(error?.data?.Message, error?.data?.Key, 'error');
            setLoading(false);
        }
    };
    const onDrawerModalEditOpen = (row?: any) => {
        SetLocalizationItem(row);
        onDrawerEditOpen();
    };

    const SetLocalizationItem = (item: any) => {
        try {
            let localizationItem: any = item;
            setSelectedLocalizationItem(localizationItem);
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };
    const GetApplicationList = async () => {
        try {
            let serviceResponse: any = await LocalizationManagementQuery.ApplicationList();
            // if (serviceResponse.isSuccess) {
            if (serviceResponse.data) {
                setApplicationList(serviceResponse.data);
            } else {
                Swal.fire('Service Error', serviceResponse.errorMessage, 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };
    const GetCultureList = async () => {
        try {
            let serviceResponse: any = await LocalizationManagementQuery.CultureList();
            // if (serviceResponse.isSuccess) {
            if (serviceResponse.data) {
                setCultureList(serviceResponse.data);
            } else {
                Swal.fire('Service Error', serviceResponse.errorMessage, 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    return (
        <SCUIContent>
            <SCUIConfirmDialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                selectedRow={selectedLocalizationItem?.keyword}
                dialogContent={dialogContent}
                dialogTitle={dialogTitle}
                buttonNameAccept={t('general.yes')}
                buttonNameReject={t('general.no')}
                actionType={actionType!}
                setDatas={setDatas}
                datas={datas}
                filterInput={filterInput}
                refresh={refreshFunc}
                e2eClassNames={locConfirmDialogE2e}
            ></SCUIConfirmDialog>
            <SCUIGeneralDrawer
                paperClassName={LOC.editDrawerPaper}
                open={drawerEditOpen}
                width={'360px'}
                onClose={(_: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerEditOpen(false);
                    }
                }}
            >
                <SCUILocalizationEdit
                    selectedRow={selectedLocalizationItem}
                    onClose={onDrawerEditClose}
                    datas={datas}
                    setDatas={setDatas}
                    refresh={refreshFunc}
                    filterInput={filterInput}
                />
            </SCUIGeneralDrawer>
            <Grid
                size={{
                    lg: 2,
                    md: 6,
                    xs: 6,
                }}
            ></Grid>
            <SCUIGeneralDrawer
                paperClassName={LOC.filterDrawerPaper}
                open={drawerFilterOpen}
                width={'360px'}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerFilterOpen(false);
                    }
                }}
            >
                <SCUILocalizationFilter
                    cultureList={cultureList}
                    onClose={onDrawerFilterClose}
                    setFilterInput={setFilterInput}
                    filterInput={filterInput}
                    applicationList={applicationList}
                    setPageNumber={setPageNumber}
                />
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                paperClassName={LOC.addDrawerPaper}
                open={state['right']}
                anchor="right"
                width={'360px'}
                onClose={(_: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setState({ ...state, right: false });
                    }
                }}
            >
                <SCUIAddLocalizationDrawerContent
                    onCloseMenu={onDrawerClose}
                    refresh={refreshFunc}
                    cultureList={cultureList}
                    applicationList={applicationList}
                />
            </SCUIGeneralDrawer>
            <Box className={LOC.page}>
            <Grid container direction="row" spacing={2}>
                <div className={LOC.breadcrumbSection}>
                    <SCUIBreadCrumb
                        title={t('dashboard.localizationManagement')}
                        componentLink={'localizationManagement'}
                        onClick={() => {
                            setDatas([]);
                        }}
                    />
                </div>
            </Grid>
            <Box className={LOC.loadingSection} sx={{ marginBottom: '16px' }}>
                <Fade in={loading} unmountOnExit>
                    <LinearProgress />
                </Fade>
            </Box>
            {datas ? (
                <Grid className={LOC.toolbarGrid} container direction="row" spacing={2}>
                    <Grid
                        className={LOC.toolbarSpacer}
                        size={{
                            sm: 10,
                            md: 10,
                            lg: 10,
                            xl: 10,
                        }}
                    >
                        {' '}
                    </Grid>
                    <Grid
                        container
                        justifyContent={'flex-end'}
                        size={{
                            sm: 1,
                            md: 1,
                            lg: 1,
                            xl: 1,
                        }}
                    >
                        <SCUIButton
                            className={LOC.inputButtonAddLocalization}
                            text={t('general.add')}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            onClick={() => {
                                onDrawerOpen();
                            }}
                        />
                    </Grid>

                    <Grid
                        className={LOC.toolbarFilterWrap}
                        container
                        justifyContent={'flex-start'}
                        size={{
                            sm: 1,
                            md: 1,
                            lg: 1,
                            xl: 1,
                        }}
                    >
                        <IconButton
                            className={LOC.inputButtonOpenFilterDrawer}
                            sx={{
                                float: 'right',
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                marginRight: 1,
                            }}
                            onClick={() => onDrawerFilterOpen()}
                        >
                            <FilterAltIcon className="svg_icons" sx={{ color: 'primary.main' }} />
                        </IconButton>
                    </Grid>

                    <Grid
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        {localizationList && localizationList.length > 0 ? (
                            <Paper
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    float: 'left',
                                    listStyle: 'none',
                                    p: 0.5,
                                    m: 0,
                                }}
                                component="ul"
                            >
                                {localizationList.map((data) => {
                                    let icon;
                                    if (!data.label.includes('All')) {
                                        return (
                                            <ListItem key={data.key}>
                                                <Chip icon={icon} label={data.label} />
                                            </ListItem>
                                        );
                                    }
                                    return <></>;
                                })}
                            </Paper>
                        ) : (
                            <div style={{ display: 'none' }}></div>
                        )}
                    </Grid>

                    <Grid
                        className={LOC.gridWrap}
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <SCUICustomDataGrid
                            onPageSizeChange={(i: number) => {
                                handlePageSize(i);
                                setFilterInput({ ...filterInput, pageSize: Number(i) });
                            }}
                            paginationModeChange
                            onPageChange={(i: number) => {
                                handlePageNumber(i);
                                setFilterInput({ ...filterInput, page: Number(i) });
                            }}
                            randomId
                            rows={datas}
                            columns={columns}
                            page={pageNumber}
                            rowCount={rowCount}
                            pageSize={pageSize}
                        />
                    </Grid>
                </Grid>
            ) : (
                <div className={LOC.emptyStateRoot}>
                    <div className={LOC.emptyStateMessageWrap} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img src={billingInfoImage} className={locCls('centeredImg', LOC.emptyStateImage)} alt="" />
                        <p
                            className={LOC.emptyStateHint}
                            style={{
                                textAlign: 'center',
                                marginTop: '3rem',
                                maxWidth: '450px',
                            }}
                        >
                            {t('localizationManagement.selectLangText')}
                        </p>
                    </div>

                    <div
                        className={LOC.wizardFormWrap}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignContent: 'center',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <FormControl className={LOC.wizardAppFieldWrap} style={{ width: 450, margin: '20px' }}>
                            <InputLabel id="labelApp">{t('general.selectApp')}</InputLabel>
                            <Select
                                className={LOC.inputSelectWizardApp}
                                label={t('general.selectApp')}
                                labelId="labelApp"
                                id="parentMenuLabel"
                                value={appId}
                                onChange={(event) => {
                                    setAppId(Number(event.target.value));
                                }}
                            >
                                {applicationList &&
                                    applicationList.map((app: any) => (
                                        <MenuItem
                                            key={app.applicationId}
                                            id={app.applicationId}
                                            value={app.applicationId}
                                        >
                                            {app.applicationName}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl className={LOC.wizardCultureFieldWrap} style={{ width: 450, margin: '20px' }}>
                            <InputLabel id="labelCulture">{t('general.selectCulture')}</InputLabel>
                            <Select
                                className={LOC.inputSelectWizardCulture}
                                label={t('general.selectCulture')}
                                labelId="labelCulture"
                                id="parentMenuLabel"
                                value={cultureCode}
                                onChange={(event) => {
                                    setCultureCode(event.target.value);
                                }}
                            >
                                {cultureList &&
                                    cultureList.map((culture: any) => (
                                        <MenuItem key={culture.cultureId} id={culture.cultureId} value={culture.code}>
                                            {culture.code}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl className={LOC.wizardKeywordFieldWrap} style={{ width: 450, margin: '10px' }}>
                            <TextField
                                className={LOC.inputTextWizardKeyword}
                                id="keywords"
                                defaultValue=""
                                type="text"
                                label={t('general.keyword')}
                                fullWidth
                                onChange={(event) => {
                                    setKeyword(event.target.value);
                                }}
                            />
                        </FormControl>
                        <FormControl className={LOC.wizardFilterButtonWrap} style={{ width: 450, margin: '10px' }}>
                            <SCUIButton
                                className={LOC.inputButtonWizardFilter}
                                text={t('general.filter')}
                                type="primary"
                                sizeType="default"
                                size={'l'}
                                onClick={() => {
                                    setFilterInput({
                                        cultureCode: cultureCode,
                                        appId: appId,
                                        keyword: keyword,
                                        page: pageNumber,
                                        pageSize: pageSize,
                                    });
                                    getLocalizationList({
                                        cultureCode: cultureCode,
                                        appId: appId,
                                        keyword: keyword,
                                    });
                                }}
                            />
                        </FormControl>
                    </div>
                </div>
            )}
            </Box>
        </SCUIContent>
    );
};
